import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database.types'
import { resetTracking } from '@/integrations/klaviyo'

export const supabase = createClient<Database>(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
)

/**
 * Convert Supabase auth errors into user-friendly messages
 * Uses Supabase error codes and status codes for reliable error detection
 *
 * @see https://supabase.com/docs/reference/javascript/auth-error-codes
 */
function getAuthErrorMessage(error: any): string {
  // Priority 1: Check for specific error codes (most reliable)
  if (error.code) {
    switch (error.code) {
      case 'user_already_exists':
        return 'An account with this email already exists'
      case 'email_not_confirmed':
        return 'Please verify your email address before logging in'
      case 'weak_password':
        return 'Password must be at least 6 characters'
      case 'over_email_send_rate_limit':
      case 'over_request_rate_limit':
        return 'Too many attempts. Please try again in a few minutes'
      case 'validation_failed':
        return 'Please check your email and password format'
      case 'signup_disabled':
        return 'New registrations are currently disabled'
      default:
        if (import.meta.env.DEV) {
          console.warn('Unhandled Supabase error code:', error.code, error)
        }
    }
  }

  // Priority 2: Check HTTP status codes
  if (error.status) {
    switch (error.status) {
      case 400:
        if (error.message?.toLowerCase().includes('invalid')) {
          return 'Invalid email or password'
        }
        return 'Invalid request. Please check your input'
      case 422:
        return 'Invalid email or password format'
      case 429:
        return 'Too many attempts. Please try again in a few minutes'
      case 500:
      case 502:
      case 503:
        return 'Server error. Please try again later'
    }
  }

  // Priority 3: Check error type/name
  if (error.name === 'AuthRetryableFetchError') {
    return 'Network error. Please check your connection and try again'
  }

  // Priority 4: Fallback to message parsing
  const msg = error.message?.toLowerCase() || ''

  if (msg.includes('invalid login credentials')) {
    return 'Invalid email or password'
  }
  if (msg.includes('not authorized')) {
    return 'You are not authorized to perform this action'
  }
  if (msg.includes('network') || msg.includes('fetch failed')) {
    return 'Network error. Please check your connection'
  }

  return error.message || 'An error occurred. Please try again'
}

export async function signIn(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) {
    throw new Error(getAuthErrorMessage(error))
  }

  // Note: Klaviyo tracking is now handled server-side via database triggers
  // See: supabase/functions/klaviyo-track-auth
}

export async function signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({ email, password })
  if (error) {
    throw new Error(getAuthErrorMessage(error))
  }
  if (!data.user) {
    throw new Error('Signup failed. Please try again.')
  }

  // Note: Klaviyo tracking is now handled server-side via database triggers
  // See: supabase/functions/klaviyo-track-auth
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error

  // Reset Klaviyo tracking on logout
  resetTracking()
}
